import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Switch, FormControlLabel, Box, Stack,
    Typography, IconButton, FormHelperText, Divider
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { RotationPattern, RotationPatternRequest } from '../../interfaces/shift/RotationPattern';
import { Shift } from '../../interfaces/shift/Shift';
import { RotationPatternService } from '../../services/rotation-pattern.service';
import { useToast } from '../../context/ToastContext';

import SearchableSelect from '../common/SearchableSelect';
interface Props {
    open: boolean;
    onClose: () => void;
    institutionId: string;
    shifts: Shift[];
    pattern?: RotationPattern | null;
    onSaved: () => void;
}

interface FormState {
    name: string;
    daysPerStep: string;
    startDate: string;
    isActive: boolean;
}

interface FormErrors {
    name?: string;
    daysPerStep?: string;
    startDate?: string;
    steps?: string;
}

const emptyForm = (): FormState => ({
    name: '',
    daysPerStep: '7',
    startDate: '',
    isActive: true,
});

const RotationPatternEditDialog: React.FC<Props> = ({
    open, onClose, institutionId, shifts, onSaved,
    // pattern prop is accepted but edit mode is not yet implemented —
    // backend has no update endpoint. Kept for future compatibility.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    pattern,
}) => {
    const toast = useToast();
    const [form, setForm] = useState<FormState>(emptyForm());
    const [errors, setErrors] = useState<FormErrors>({});
    const [steps, setSteps] = useState<Shift[]>([]);
    const [selectedShiftId, setSelectedShiftId] = useState<string>('');
    const [saving, setSaving] = useState(false);

    const activeShifts = shifts.filter(s => s.isActive);

    useEffect(() => {
        if (open) {
            setForm(emptyForm());
            setErrors({});
            setSteps([]);
            setSelectedShiftId(activeShifts.length > 0 ? activeShifts[0].id : '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm(prev => ({ ...prev, [key]: value }));
        setErrors(prev => ({ ...prev, [key]: undefined }));
    };

    const handleAddStep = () => {
        if (!selectedShiftId) return;
        const shift = shifts.find(s => s.id === selectedShiftId);
        if (!shift) return;
        setSteps(prev => [...prev, shift]);
        setErrors(prev => ({ ...prev, steps: undefined }));
    };

    const handleMoveUp = (index: number) => {
        if (index === 0) return;
        setSteps(prev => {
            const next = [...prev];
            [next[index - 1], next[index]] = [next[index], next[index - 1]];
            return next;
        });
    };

    const handleMoveDown = (index: number) => {
        setSteps(prev => {
            if (index === prev.length - 1) return prev;
            const next = [...prev];
            [next[index], next[index + 1]] = [next[index + 1], next[index]];
            return next;
        });
    };

    const handleRemoveStep = (index: number) => {
        setSteps(prev => prev.filter((_, i) => i !== index));
    };

    const validate = (): boolean => {
        const errs: FormErrors = {};

        if (!form.name.trim()) {
            errs.name = 'Name is required';
        } else if (form.name.trim().length > 80) {
            errs.name = 'Name must be 80 characters or fewer';
        }

        const days = Number(form.daysPerStep);
        if (!form.daysPerStep || isNaN(days) || days < 1 || days > 90) {
            errs.daysPerStep = 'Days per step must be between 1 and 90';
        }

        if (!form.startDate) {
            errs.startDate = 'Start date is required';
        }

        if (steps.length === 0) {
            errs.steps = 'At least one shift step is required';
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        const body: RotationPatternRequest = {
            name: form.name.trim(),
            daysPerStep: Number(form.daysPerStep),
            startDate: form.startDate,
            isActive: form.isActive,
            shiftIdsInOrder: steps.map(s => s.id),
        };

        setSaving(true);
        try {
            await RotationPatternService.create(institutionId, body);
            toast.showSuccess('Rotation pattern created successfully');
            onSaved();
            onClose();
        } catch (err) {
            toast.showApiError(err, 'Failed to save rotation pattern');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'shifts.pattern.page' } as any} maxWidth="sm" fullWidth>
            <DialogTitle>New Rotation Pattern</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <Stack data-help-section="shifts.pattern.section.details" spacing={2}>
                        <TextField
                            data-help="shifts.pattern.field.name" label="Name"
                            fullWidth
                            value={form.name}
                            onChange={e => setField('name', e.target.value)}
                            error={!!errors.name}
                            helperText={errors.name}
                            required
                            inputProps={{ 'data-testid': 'pattern-name', maxLength: 80 }}
                        />

                        <TextField
                            data-help="shifts.pattern.field.daysPerStep" label="Days per Step"
                            type="number"
                            fullWidth
                            value={form.daysPerStep}
                            onChange={e => setField('daysPerStep', e.target.value)}
                            error={!!errors.daysPerStep}
                            helperText={errors.daysPerStep || 'How many days each step lasts (1–90)'}
                            required
                            inputProps={{ min: 1, max: 90, 'data-testid': 'pattern-days-per-step' }}
                        />

                        <TextField
                            data-help="shifts.pattern.field.startDate" label="Start Date"
                            type="date"
                            fullWidth
                            value={form.startDate}
                            onChange={e => setField('startDate', e.target.value)}
                            error={!!errors.startDate}
                            helperText={errors.startDate}
                            required
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ 'data-testid': 'pattern-start-date' }}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.isActive}
                                    onChange={e => setField('isActive', e.target.checked)}
                                    data-testid="pattern-active"
                                />
                            }
                            data-help="shifts.pattern.field.active" label="Active"
                        />

                        <Divider />

                        <Typography data-help-section="shifts.pattern.section.steps" variant="subtitle2">Shift Steps (in rotation order)</Typography>

                        {steps.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No steps yet — add at least one shift.
                            </Typography>
                        ) : (
                            <Stack spacing={1}>
                                {steps.map((step, index) => (
                                    <Box
                                        key={`${step.id}-${index}`}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            p: 1,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ minWidth: 24, fontWeight: 'bold', color: 'text.secondary' }}
                                        >
                                            {index + 1}
                                        </Typography>
                                        <Typography variant="body2" sx={{ flex: 1 }}>
                                            {step.code} — {step.name}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            data-help="shifts.pattern.moveUp" onClick={() => handleMoveUp(index)}
                                            disabled={index === 0}
                                            aria-label="move up"
                                        >
                                            <ArrowUpwardIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            data-help="shifts.pattern.moveDown" onClick={() => handleMoveDown(index)}
                                            disabled={index === steps.length - 1}
                                            aria-label="move down"
                                        >
                                            <ArrowDownwardIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            data-help="shifts.pattern.removeStep" onClick={() => handleRemoveStep(index)}
                                            color="error"
                                            aria-label="remove step"
                                        >
                                            <RemoveCircleOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Stack>
                        )}

                        {errors.steps && (
                            <FormHelperText error>{errors.steps}</FormHelperText>
                        )}

                        {activeShifts.length > 0 ? (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <SearchableSelect
                                    options={activeShifts}
                                    value={selectedShiftId}
                                    onChange={id => setSelectedShiftId(id)}
                                    getOptionLabel={s => `${s.code} — ${s.name}`}
                                    data-help="shifts.pattern.field.addStep" label="Add step"
                                    data-testid="add-step-select"
                                    sx={{ flex: 1 }}
                                />
                                <Button
                                    variant="outlined"
                                    data-help="shifts.pattern.addStep" onClick={handleAddStep}
                                    disabled={!selectedShiftId}
                                    data-testid="add-step-button"
                                    sx={{ whiteSpace: 'nowrap', alignSelf: 'center' }}
                                >
                                    Add
                                </Button>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No active shifts available. Create active shifts first.
                            </Typography>
                        )}
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button data-help="shifts.pattern.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button data-help="shifts.pattern.save" onClick={handleSubmit} variant="contained" disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RotationPatternEditDialog;
